/* localStorage persistence for the shop cart (CUSTOMER FIX Stage 7).
 *
 * - Storage: localStorage keyed by `(tenantCode, kioskId)`.
 * - Legacy sessionStorage carts are migrated once on first access.
 * - Cleared on payment success, "Vyprázdnit", or explicit clear.
 * - Payload: line items + quantities only — no tokens, no PII.
 *
 * FIX 4.3 — same-tab cart signal:
 *  - After successful save/clear, dispatch window CustomEvent
 *    `customer:shop-cart-changed` with detail `{ tenantCode, kioskId }`.
 *  - Do NOT use StorageEvent (same-tab writes never fire it).
 *  - Do NOT overload the cross-tab `cart-reset` bus.
 */

import type { ShopCart } from '../types.js';
import { upsertCartRegistryEntry } from './cartRegistryStorage.js';
import { ensureCartRegistryBootstrapped } from './cartRegistry.js';
import { createEmptyCart } from './cartLogic.js';
import { migrateSessionCartsToLocalStorage } from './migrateSessionCarts.js';
import { validatePersistedCartLines } from './persistedCartValidation.js';

export { isValidPersistedCartLine, validatePersistedCartLines } from './persistedCartValidation.js';

export type SaveCartResult =
  | { readonly ok: true }
  | {
      readonly ok: false;
      readonly reason: 'storage_unavailable' | 'quota' | 'serialization';
    };

const STORAGE_PREFIX = 'rpapp-customer.shop.cart';
const STORAGE_VERSION = 1;

/** Window CustomEvent name for same-tab shop cart persistence changes (FIX 4.3). */
export const CUSTOMER_SHOP_CART_CHANGED_EVENT = 'customer:shop-cart-changed' as const;

export interface CustomerShopCartChangedDetail {
  readonly tenantCode: string;
  readonly kioskId: number;
}

export interface SaveCartMetadata {
  readonly tenantName?: string;
  readonly salesPointLabel?: string;
  readonly shopHref?: string;
}

function emitShopCartChanged(tenantCode: string, kioskId: number): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(
    new CustomEvent<CustomerShopCartChangedDetail>(CUSTOMER_SHOP_CART_CHANGED_EVENT, {
      detail: { tenantCode, kioskId },
    }),
  );
}

interface PersistedCartV1 {
  v: 1;
  lines: ShopCart['lines'];
}

export function buildStorageKey(tenantCode: string, kioskId: number): string {
  return `${STORAGE_PREFIX}:${tenantCode}:${kioskId}`;
}

function safeStorage(): Storage | null {
  migrateSessionCartsToLocalStorage();
  ensureCartRegistryBootstrapped();
  if (typeof window === 'undefined' || !('localStorage' in window)) {
    return null;
  }
  try {
    const probeKey = `${STORAGE_PREFIX}:probe`;
    window.localStorage.setItem(probeKey, '1');
    window.localStorage.removeItem(probeKey);
    return window.localStorage;
  } catch {
    return null;
  }
}

function syncRegistry(
  tenantCode: string,
  kioskId: number,
  cart: ShopCart,
  metadata?: SaveCartMetadata,
): void {
  const totalQuantity =
    cart.totalQuantity > 0
      ? cart.totalQuantity
      : cart.lines.reduce((sum, line) => sum + line.quantity, 0);
  upsertCartRegistryEntry({
    tenantCode,
    kioskId,
    totalQuantity,
    tenantName: metadata?.tenantName,
    salesPointLabel: metadata?.salesPointLabel,
    shopHref: metadata?.shopHref,
  });
}

function isQuotaExceededError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'QuotaExceededError') {
    return true;
  }
  return (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    (error as { name: string }).name === 'QuotaExceededError'
  );
}

export function loadCart(tenantCode: string, kioskId: number): ShopCart {
  const storage = safeStorage();
  if (storage === null) {
    return createEmptyCart();
  }
  const raw = storage.getItem(buildStorageKey(tenantCode, kioskId));
  if (raw === null) {
    return createEmptyCart();
  }
  try {
    const parsed = JSON.parse(raw) as PersistedCartV1;
    if (parsed.v !== STORAGE_VERSION) {
      return createEmptyCart();
    }
    const lines = validatePersistedCartLines(parsed.lines);
    if (lines === null) {
      return createEmptyCart();
    }
    let totalAmountMajor = 0;
    let totalQuantity = 0;
    for (const line of lines) {
      totalAmountMajor += line.unitPrice * line.quantity;
      totalQuantity += line.quantity;
    }
    const totalAmountMinor = Math.round(totalAmountMajor * 100);
    return {
      lines,
      totalAmountMajor: totalAmountMinor / 100,
      totalAmountMinor,
      totalQuantity,
    };
  } catch {
    return createEmptyCart();
  }
}

export function saveCart(
  tenantCode: string,
  kioskId: number,
  cart: ShopCart,
  metadata?: SaveCartMetadata,
): SaveCartResult {
  const storage = safeStorage();
  if (storage === null) {
    return { ok: false, reason: 'storage_unavailable' };
  }
  const payload: PersistedCartV1 = { v: STORAGE_VERSION, lines: cart.lines };
  let serialized: string;
  try {
    serialized = JSON.stringify(payload);
  } catch {
    return { ok: false, reason: 'serialization' };
  }
  try {
    storage.setItem(buildStorageKey(tenantCode, kioskId), serialized);
    syncRegistry(tenantCode, kioskId, cart, metadata);
    emitShopCartChanged(tenantCode, kioskId);
    return { ok: true };
  } catch (error) {
    if (isQuotaExceededError(error)) {
      return { ok: false, reason: 'quota' };
    }
    return { ok: false, reason: 'serialization' };
  }
}

export function clearStoredCart(tenantCode: string, kioskId: number): void {
  const storage = safeStorage();
  if (storage === null) {
    return;
  }
  try {
    storage.removeItem(buildStorageKey(tenantCode, kioskId));
    syncRegistry(tenantCode, kioskId, createEmptyCart());
    emitShopCartChanged(tenantCode, kioskId);
  } catch {
    // ignore
  }
}
